import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, Star } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getReviewShowcase } from "@/lib/api";
import { bookingImg } from "@/lib/format";

export const GuestVoices = ({ zone = null, zoneName = null }) => {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    setI(0);
    getReviewShowcase(lang, zone).then(setItems).catch(() => {});
  }, [lang, zone]);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items]);

  if (!items.length) return null;
  const r = items[Math.min(i, items.length - 1)];

  return (
    <section data-testid="guest-voices-section" className="bg-sand py-20 lg:py-28">
      <div className="mx-auto max-w-5xl px-5 text-center lg:px-8">
        <p className="overline text-terracotta">{t("sections.guest_voices_over")}</p>
        <h2 className="mt-2 font-serif text-4xl font-medium text-petrol lg:text-5xl">
          {t("sections.guest_voices_title")}{zoneName ? ` · ${zoneName}` : ""}
        </h2>

        <div className="relative mt-12 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="guest-voice-card"
              className="flex flex-col items-center"
            >
              <Quote className="text-gold" size={40} />
              <p className="mt-6 max-w-3xl font-serif text-2xl italic leading-relaxed text-petrol lg:text-3xl">
                &ldquo;{r.text}&rdquo;
              </p>
              <div className="mt-8 flex items-center gap-4">
                {r.image && <img src={bookingImg(r.image, "square240")} alt={r.hotel} loading="lazy" decoding="async" className="h-14 w-14 rounded-none object-cover" />}
                <div className="text-left">
                  <p className="font-semibold text-petrol">
                    {r.author}{r.country ? ` · ${r.country}` : ""}
                  </p>
                  <Link to={`/stay/${r.slug}`} className="text-sm text-terracotta hover:underline">
                    {r.hotel}
                  </Link>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-petrol/60">
                    <Star size={11} className="fill-gold text-gold" /> {r.rating} · {r.zone_name}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {items.map((_, idx) => (
            <button
              key={idx}
              data-testid={`guest-voice-dot-${idx}`}
              onClick={() => setI(idx)}
              aria-label={`review ${idx + 1}`}
              className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-terracotta" : "w-2 bg-petrol/20 hover:bg-petrol/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
